import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import EmptyState from "../../../components/common/EmptyState";
import { fetchAuditTrail, AuditEntry } from "../api";

const ACTION_STYLE: Record<string, string> = {
  CREATE: "bg-green-100 text-green-800",
  UPDATE: "bg-blue-100 text-blue-800",
  DELETE: "bg-red-100 text-red-800",
};

const ENTITY_TYPES = [
  { value: "", label: "All" },
  { value: "compliance-register", label: "Compliance Register" },
  { value: "inspection-record", label: "Inspection Records" },
  { value: "contractor", label: "Contractors" },
  { value: "evidence-file", label: "Evidence Files" },
];

const AuditTrailPage: React.FC = () => {
  const [items, setItems] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [entityType, setEntityType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = async (cursor?: string) => {
    try {
      if (!cursor) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      const params: Record<string, string | number | undefined> = {
        pageSize: 50,
        cursor,
      };
      if (entityType) params.entityType = entityType;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const result = await fetchAuditTrail(
        params as Parameters<typeof fetchAuditTrail>[0],
      );

      if (cursor) {
        setItems((prev) => [...prev, ...result.items]);
      } else {
        setItems(result.items);
      }
      setNextCursor(result.nextCursor);
    } catch {
      setError("Failed to load audit trail.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, startDate, endDate]);

  const toggleExpand = (key: string) => {
    setExpandedId(expandedId === key ? null : key);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit Trail"
        description="Immutable record of all changes to compliance data."
      />

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-3 flex flex-wrap gap-3 items-end">
        <div>
          <label className="form-label">Entity type</label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="form-input"
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="form-input"
          />
        </div>
        <div>
          <label className="form-label">To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="form-input"
          />
        </div>
      </div>

      {error && <ErrorAlert message={error} onRetry={() => load()} />}
      {loading && <Spinner />}

      {!loading && items.length === 0 && (
        <EmptyState
          title="No audit entries"
          description="Changes to compliance data will be recorded here automatically."
        />
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((entry, i) => {
            const key = `${entry.timestamp}#${entry.entityType}#${entry.entityId}`;
            const isExpanded = expandedId === key;

            return (
              <div key={i} className="bg-white shadow rounded-lg p-3">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleExpand(key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && toggleExpand(key)}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        ACTION_STYLE[entry.action] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {entry.action}
                    </span>
                    <span className="text-sm text-gray-900 font-medium capitalize">
                      {entry.entityType.replace(/-/g, " ")}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">
                      {entry.entityId.slice(0, 8)}…
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {entry.userId}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(entry.timestamp).toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-400">
                      {isExpanded ? "▲" : "▼"}
                    </span>
                  </div>
                </div>

                {isExpanded && !entry.truncated && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {entry.before !== undefined && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          Before
                        </p>
                        <pre className="text-xs bg-gray-50 rounded p-2 overflow-auto max-h-48">
                          {entry.before
                            ? JSON.stringify(entry.before, null, 2)
                            : "null"}
                        </pre>
                      </div>
                    )}
                    {entry.after !== undefined && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          After
                        </p>
                        <pre className="text-xs bg-gray-50 rounded p-2 overflow-auto max-h-48">
                          {entry.after
                            ? JSON.stringify(entry.after, null, 2)
                            : "null"}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                {isExpanded && entry.truncated && (
                  <p className="mt-2 text-xs text-amber-600">
                    Before/after values omitted (entry exceeded size limit).
                  </p>
                )}
              </div>
            );
          })}

          {nextCursor && (
            <div className="text-center py-4">
              <button
                onClick={() => load(nextCursor)}
                disabled={loadingMore}
                className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md disabled:opacity-50"
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditTrailPage;
